import React, { useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { designTokens, getTypography } from "../../styles/designTokens";

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(6px);
  z-index: 1000;
  animation: ${fadeIn} 0.25s ease forwards;
`;

const ModalContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(220, 220, 230, 0.4);
  border-radius: 14px;
  padding: 32px 24px;
  width: fit-content; /* 내용에 따라 width가 자동으로 늘어남 */
  min-width: 360px; /* 최소 너비 */
  max-width: 90vw; /* 화면 폭의 90%를 넘지 않도록 제한 */
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 10px 28px rgba(10, 20, 40, 0.1);
`;

const Title = styled.h3`
  ${getTypography("korean", "title2")};
  color: ${designTokens.colors.font.primary};
  text-align: center;
  margin: 0;
`;

const Message = styled.p`
  ${getTypography("korean", "body2")};
  color: ${designTokens.colors.font.secondary};
  text-align: center;
  margin: 0;
  white-space: pre-line;
`;

const Button = styled.button`
  ${getTypography("korean", "body4")};
  width: 100px;
  height: 42px;
  margin: 0 auto;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #5c6eff, #7385ff);
  color: white;
  cursor: pointer;
  transition: 0.2s ease;
  &:hover { filter: brightness(1.05); }
  &:active { transform: translateY(1px); }
`;

interface InfoModalProps {
  title?: string;
  message: string;
  onClose: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({ title, message, onClose }) => {
  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape" || e.key === "Enter") onClose();
  }, [onClose]);

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {title && <Title>{title}</Title>}
        <Message>{message}</Message>
        <Button onClick={onClose}>확인</Button>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default InfoModal;
